import { Link, router } from '@inertiajs/react'
import { useState } from 'react'
import SystemAdminLayout from '@/layouts/SystemAdminLayout'

interface Member {
  user_id: number
  email_address: string
  roles: string[]
  active: boolean
  email_confirmed: boolean
  joined_at: string
}

interface SurveyMeta {
  id: number
  name: string
  status: string
  anonymity: string | null
  response_count: number
  has_webhook: boolean
  created_at: string
  published_at: string | null
}

interface ApiKeyRow {
  id: number
  label: string
  scope: string
  masked_token: string
  revoked_at: string | null
  last_used_at: string | null
}

interface Delivery {
  id: number
  event: string
  url: string
  status_code: number | null
  succeeded: boolean
  attempt_number: number
  delivered_at: string | null
}

interface Props {
  account: {
    id: number
    name: string
    organization_type: string | null
    currency: string
    created_at: string
    billing: {
      plan: string
      plan_status: string
      trial_ends_at: string | null
      comped_until: string | null
      comped: boolean
      access_active: boolean
      read_only: boolean
      subscription: { id: number; status: string; processor_plan: string; trial_ends_at: string | null } | null
    }
    members: Member[]
    surveys: SurveyMeta[]
    api_keys: ApiKeyRow[]
    webhook_deliveries: Delivery[]
    // Account-default authentication method (D1: password | otp | idp).
    auth_method: string
    // Per-role overrides. Null when no overrides set; keys ⊆ owner/admin/member,
    // values password | otp. A missing/blank role inherits the account default.
    role_auth_methods: Record<string, string> | null
  }
}

const AUTH_ROLES = ['owner', 'admin', 'member'] as const
type AuthRole = (typeof AUTH_ROLES)[number]

const ROLE_LABELS: Record<AuthRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
}

function date(s: string | null): string {
  return s ? new Date(s).toLocaleDateString() : '—'
}

function AuthenticationCard({
  accountId,
  authMethod,
  roleAuthMethods,
}: {
  accountId: number
  authMethod: string
  roleAuthMethods: Record<string, string> | null
}) {
  const [authValue, setAuthValue] = useState(authMethod)
  const [roleValues, setRoleValues] = useState<Record<AuthRole, string>>(() => ({
    owner: roleAuthMethods?.owner ?? '',
    admin: roleAuthMethods?.admin ?? '',
    member: roleAuthMethods?.member ?? '',
  }))
  const [saving, setSaving] = useState(false)

  const save = () => {
    setSaving(true)
    router.post(
      `/system_admin/accounts/${accountId}/auth_settings`,
      // Empty string on a role means "inherit the account default".
      { account: { auth_method: authValue, role_auth_methods: roleValues } },
      { preserveScroll: true, onFinish: () => setSaving(false) }
    )
  }

  return (
    <div className="card bg-base-100">
      <div className="card-body space-y-4">
        <h2 className="card-title text-base">Authentication</h2>

        <div className="alert alert-soft alert-info text-sm">
          <span>Controls how this organization&apos;s members sign in. Platform operation.</span>
        </div>

        <fieldset className="fieldset">
          <legend className="fieldset-legend">Account default</legend>
          <select
            className="select w-full"
            value={authValue}
            onChange={(e) => setAuthValue(e.target.value)}
          >
            <option value="password">Password</option>
            <option value="otp">Email code (OTP)</option>
            <option value="idp" disabled>
              SSO — coming soon
            </option>
          </select>
        </fieldset>

        <div className="space-y-2">
          <p className="text-sm font-medium text-base-content/70">Per-role overrides</p>
          <p className="text-xs text-base-content/50">
            Leave a role on &ldquo;Inherit account default&rdquo; to follow the setting above. The
            strictest method across a member&apos;s roles wins.
          </p>
          {AUTH_ROLES.map((role) => (
            <fieldset key={role} className="fieldset">
              <legend className="fieldset-legend">{ROLE_LABELS[role]}</legend>
              <select
                className="select select-sm w-full"
                value={roleValues[role]}
                onChange={(e) => setRoleValues((prev) => ({ ...prev, [role]: e.target.value }))}
              >
                <option value="">Inherit account default</option>
                <option value="password">Password</option>
                <option value="otp">Email code (OTP)</option>
              </select>
            </fieldset>
          ))}
        </div>

        <div className="flex justify-end">
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
            {saving && <span className="loading loading-spinner loading-xs" />}
            Save authentication
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Show({ account }: Props) {
  const [trialDays, setTrialDays] = useState(14)
  const [compMonths, setCompMonths] = useState(3)
  const url = (action: string) => `/system_admin/accounts/${account.id}/${action}`

  const act = (action: string, params: Record<string, number> = {}, confirmMsg?: string) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return
    router.post(url(action), params)
  }

  return (
    <SystemAdminLayout>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{account.name}</h1>
          <p className="text-sm text-base-content/60">
            {account.organization_type} · {account.currency} · created {date(account.created_at)}
          </p>
        </div>
        <Link href="/system_admin/accounts" className="btn btn-ghost btn-sm">
          <span className="iconify lucide--arrow-left size-4" /> All organizations
        </Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Members */}
          <div className="card bg-base-100">
            <div className="card-body p-0">
              <h2 className="card-title text-base px-6 pt-5">Members</h2>
              <table className="table table-sm">
                <thead><tr><th>Email</th><th>Role</th><th>Status</th><th>Joined</th></tr></thead>
                <tbody>
                  {account.members.map((m) => (
                    <tr key={m.user_id}>
                      <td>
                        <Link href={`/system_admin/users/${m.user_id}`} className="link link-hover">{m.email_address}</Link>
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {m.roles.map((role) => (
                            <span key={role} className="badge badge-soft badge-sm">{role}</span>
                          ))}
                        </div>
                      </td>
                      <td className="text-xs">
                        {m.email_confirmed ? 'confirmed' : <span className="text-warning">unconfirmed</span>}
                        {!m.active && <span className="text-error ms-1">inactive</span>}
                      </td>
                      <td className="text-sm">{date(m.joined_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Surveys — metadata only, never response content */}
          <div className="card bg-base-100">
            <div className="card-body p-0">
              <h2 className="card-title text-base px-6 pt-5">Surveys</h2>
              <table className="table table-sm">
                <thead><tr><th>Name</th><th>Status</th><th>Mode</th><th className="text-end">Responses</th><th>Webhook</th><th>Published</th></tr></thead>
                <tbody>
                  {account.surveys.map((s) => (
                    <tr key={s.id}>
                      <td className="font-medium">{s.name}</td>
                      <td><span className={`badge badge-soft badge-sm ${s.status === 'active' ? 'badge-success' : s.status === 'closed' ? 'badge-neutral' : ''}`}>{s.status}</span></td>
                      <td className="text-sm">{s.anonymity ?? <span className="text-base-content/40">—</span>}</td>
                      <td className="text-end tabular-nums">{s.response_count}</td>
                      <td>{s.has_webhook ? <span className="iconify lucide--webhook size-4 text-success" /> : <span className="text-base-content/30">—</span>}</td>
                      <td className="text-sm">{date(s.published_at)}</td>
                    </tr>
                  ))}
                  {account.surveys.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-base-content/40 py-6">No surveys yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Webhook deliveries */}
          {account.webhook_deliveries.length > 0 && (
            <div className="card bg-base-100">
              <div className="card-body p-0">
                <h2 className="card-title text-base px-6 pt-5">Recent webhook deliveries</h2>
                <table className="table table-sm">
                  <thead><tr><th>Event</th><th>Status</th><th>Attempt</th><th>Delivered</th></tr></thead>
                  <tbody>
                    {account.webhook_deliveries.map((d) => (
                      <tr key={d.id}>
                        <td className="font-mono text-xs">{d.event}</td>
                        <td>
                          <span className={`badge badge-soft badge-sm ${d.succeeded ? 'badge-success' : 'badge-error'}`}>
                            {d.status_code ?? 'error'}
                          </span>
                        </td>
                        <td className="tabular-nums">{d.attempt_number}</td>
                        <td className="text-sm">{d.delivered_at ? new Date(d.delivered_at).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Billing */}
          <div className="card bg-base-100">
            <div className="card-body space-y-2">
              <h2 className="card-title text-base">Billing</h2>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                <dt className="text-base-content/60">Plan</dt><dd>{account.billing.plan}</dd>
                <dt className="text-base-content/60">Status</dt><dd>{account.billing.plan_status}</dd>
                <dt className="text-base-content/60">Access</dt>
                <dd>{account.billing.access_active ? <span className="text-success">active</span> : <span className="text-error">read-only</span>}</dd>
                <dt className="text-base-content/60">Trial ends</dt><dd>{date(account.billing.trial_ends_at)}</dd>
                <dt className="text-base-content/60">Comped until</dt><dd>{date(account.billing.comped_until)}</dd>
                {account.billing.subscription && (
                  <>
                    <dt className="text-base-content/60">Stripe sub</dt>
                    <dd className="text-xs">{account.billing.subscription.status} · <span className="font-mono">{account.billing.subscription.processor_plan}</span></dd>
                  </>
                )}
              </dl>
            </div>
          </div>

          {/* Support actions */}
          <div className="card bg-base-100">
            <div className="card-body space-y-4">
              <h2 className="card-title text-base">Support actions</h2>

              {!account.billing.subscription && (
                <div className="space-y-1">
                  <label className="text-xs text-base-content/60">Extend trial (days)</label>
                  <div className="join w-full">
                    <input type="number" min={1} max={365} className="input input-sm join-item w-full"
                      value={trialDays} onChange={(e) => setTrialDays(Number(e.target.value))} />
                    <button className="btn btn-sm join-item" onClick={() => act('extend_trial', { days: trialDays })}>
                      Extend
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <label className="text-xs text-base-content/60">Comp (months)</label>
                <div className="join w-full">
                  <input type="number" min={1} max={120} className="input input-sm join-item w-full"
                    value={compMonths} onChange={(e) => setCompMonths(Number(e.target.value))} />
                  <button className="btn btn-sm join-item" onClick={() => act('comp', { months: compMonths })}>
                    Comp
                  </button>
                </div>
                <p className="text-xs text-base-content/50">Free access regardless of plan state. Survives billing sync.</p>
              </div>

              <div className="divider my-0" />

              <div className="flex flex-col gap-2">
                <button className="btn btn-ghost btn-sm text-error justify-start"
                  onClick={() => act('expire', {}, `Expire ${account.name}? They lose write access immediately (data preserved).`)}>
                  <span className="iconify lucide--circle-off size-4" /> Expire organization
                </button>
                <button className="btn btn-ghost btn-sm text-error justify-start"
                  onClick={() => act('cancel', {}, `Cancel ${account.name}? ${account.billing.subscription ? 'Their Stripe subscription is canceled immediately.' : 'No subscription — this expires the org.'}`)}>
                  <span className="iconify lucide--ban size-4" /> Cancel subscription
                </button>
              </div>
            </div>
          </div>

          {/* Authentication (platform op — system_admin only) */}
          <AuthenticationCard
            accountId={account.id}
            authMethod={account.auth_method}
            roleAuthMethods={account.role_auth_methods}
          />

          {/* API keys */}
          <div className="card bg-base-100">
            <div className="card-body space-y-2">
              <h2 className="card-title text-base">API keys</h2>
              {account.api_keys.length === 0 ? (
                <p className="text-sm text-base-content/50">None.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {account.api_keys.map((k) => (
                    <li key={k.id} className="flex items-center justify-between gap-2">
                      <div>
                        <div className="font-medium">{k.label} <span className="badge badge-soft badge-sm ms-1">{k.scope}</span></div>
                        <div className="font-mono text-xs text-base-content/50">{k.masked_token}</div>
                      </div>
                      {k.revoked_at
                        ? <span className="badge badge-soft badge-error badge-sm">revoked</span>
                        : <span className="badge badge-soft badge-success badge-sm">active</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </SystemAdminLayout>
  )
}
